
import Vue, { PropType } from "vue";
import { mapActions } from "vuex";
import yomisukeIcon from "@/assets/img/manuscript-analysis/yomisuke_icon.png";
import { logWarn, logError } from "@/lib/utils/logger";

interface Props {
  id: string;
  feedbackPreview: string;
  createdAt: Date | string;
  clickable?: boolean;
  showStatus?: boolean;
  status?: "completed" | "processing" | "failed";
  questionContent?: string;
  requestTitle?: string;
  writerType?: string;
  completed?: boolean;
  isError?: boolean;
}

interface Data {}

interface Computed {
  formattedDate: string;
  formattedDateTime: string;
  isClickable: boolean;
  shouldShowStatus: boolean;
  currentStatus: "completed" | "processing" | "failed";
  statusText: string;
  iconImageUrl: string;
}

interface Methods {
  handleClick: (event: MouseEvent | KeyboardEvent) => void;
  showResultWithId: (payload: { id: string }) => Promise<void>;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  name: "AssistHistoryCard",
  props: {
    id: {
      type: String as PropType<string>,
      required: true,
    },
    feedbackPreview: {
      type: String as PropType<string>,
      required: true,
    },
    createdAt: {
      type: [Date, String] as PropType<Date | string>,
      required: true,
    },
    clickable: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    showStatus: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
    status: {
      type: String as PropType<"completed" | "processing" | "failed">,
      default: "completed",
    },
    questionContent: {
      type: String as PropType<string>,
      default: "",
    },
    requestTitle: {
      type: String as PropType<string>,
      default: "",
    },
    writerType: {
      type: String as PropType<string>,
      default: "",
    },
    completed: {
      type: Boolean as PropType<boolean>,
      default: true,
    },
    isError: {
      type: Boolean as PropType<boolean>,
      default: false,
    },
  },
  computed: {
    // 日時フォーマット（Figma仕様: 2024/3/12 11:29）
    formattedDate(): string {
      // 安全なDate変換処理
      const date = this.createdAt instanceof Date ? this.createdAt : new Date(this.createdAt);
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      const day = date.getDate();
      const hours = date.getHours().toString().padStart(2, "0");
      const minutes = date.getMinutes().toString().padStart(2, "0");

      return `${year}/${month}/${day} ${hours}:${minutes}`;
    },
    formattedDateTime(): string {
      // 安全なDate変換処理
      const date = this.createdAt instanceof Date ? this.createdAt : new Date(this.createdAt);
      return date.toISOString();
    },
    // 動的なクリック可能状態
    isClickable(): boolean {
      return this.clickable && this.completed && !this.isError;
    },
    // 動的なステータス表示
    shouldShowStatus(): boolean {
      return this.showStatus || !this.completed || this.isError;
    },
    // 現在の状態に基づくステータス
    currentStatus(): "completed" | "processing" | "failed" {
      if (this.isError) return "failed";
      if (!this.completed) return "processing";
      return "completed";
    },
    // ステータステキスト
    statusText(): string {
      switch (this.currentStatus) {
        case "processing":
          return "処理中";
        case "failed":
          return "失敗";
        case "completed":
        default:
          return "完了";
      }
    },
    iconImageUrl(): string {
      // writerTypeがnullまたは空文字の場合はローカルのデフォルトアイコンを使用
      if (!this.writerType) {
        return yomisukeIcon;
      }

      // writerTypeを小文字に変換してURLを生成
      const writerTypeLower = this.writerType.toLowerCase();
      return `${process.env.VUE_APP_NOLA_WEB!}/writer-type-indicator/images/result/cats/${writerTypeLower}.png`;
    },
  },
  methods: {
    ...mapActions("assistNavigationModule", ["showResultWithId"]),
    // イベントハンドラー
    async handleClick(event: MouseEvent | KeyboardEvent): Promise<void> {
      if (this.isClickable) {
        try {
          // 🔧 履歴データの読み込み状況を確認
          const historyList = this.$store.getters["assistHistory/filteredHistoryList"];
          const targetItem = historyList.find((item: any) => item.id === this.id);

          if (!targetItem) {
            logWarn(
              { module: "AssistHistoryCard", action: "handleClick" },
              "対象アイテムが見つかりません。履歴データを再読み込みします。",
              { id: this.id }
            );
            // 履歴データが見つからない場合は読み込みを実行
            await this.$store.dispatch("assistHistory/fetchHistoryList");
          }

          const currentTab = this.$store.getters["assistNavigationModule/currentTab"];
          if (currentTab !== "assist") {
            await this.$store.dispatch("assistNavigationModule/setCurrentTab", "assist");
          }

          // assist-navigationモジュールのshowResultWithIdアクションを呼び出し
          await this.showResultWithId({ id: this.id });

          // 従来のclickイベントも発火（親コンポーネントでの追加処理用）
          this.$emit("click", event);
        } catch (error) {
          logError({ module: "AssistHistoryCard", action: "handleClick" }, "結果画面への遷移に失敗", error);
          // エラーが発生した場合でも従来のイベントは発火
          this.$emit("click", event);
        }
      }
    },
  },
});

export type AssistHistoryCardProps = Props;
